import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { carePlansApi } from "@/api/careplans";
import { caregiversApi, clientsApi } from "@/api/directory";
import { visitsApi } from "@/api/scheduling";
import { Alert } from "@/components/Alert";
import { FormSelect } from "@/components/FormSelect";
import { Modal } from "@/components/Modal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const STATUS_OPTIONS = [
  { value: "scheduled", label: "Scheduled" },
  { value: "confirmed", label: "Confirmed" },
  { value: "in_progress", label: "In Progress" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
];

export function VisitModal({ visit, initialDate, initialTime, onClose, onSaved }) {
  const isEditing = Boolean(visit?.id);

  const [clients, setClients] = useState([]);
  const [caregivers, setCaregivers] = useState([]);
  const [clientPlans, setClientPlans] = useState([]);
  const [carePlanId, setCarePlanId] = useState("");
  const [loadingOptions, setLoadingOptions] = useState(true);

  const [clientId, setClientId] = useState(visit?.client || "");
  const [caregiverId, setCaregiverId] = useState(visit?.caregiver || "");
  const [title, setTitle] = useState(visit?.title || "");
  const [serviceType, setServiceType] = useState(visit?.service_type || "Personal Care");
  const [visitDate, setVisitDate] = useState(() => {
    if (visit?.start_time) return visit.start_time.split("T")[0];
    if (initialDate) return initialDate;
    return new Date().toISOString().split("T")[0];
  });
  const [startTime, setStartTime] = useState(() => {
    if (visit?.start_time) return visit.start_time.split("T")[1].substring(0, 5);
    if (initialTime) return initialTime;
    return "09:00";
  });
  const [endTime, setEndTime] = useState(() => {
    if (visit?.end_time) return visit.end_time.split("T")[1].substring(0, 5);
    if (initialTime) {
      const [h, m] = initialTime.split(":").map(Number);
      const endH = String(Math.min(h + 2, 23)).padStart(2, "0");
      return `${endH}:${String(m).padStart(2, "0")}`;
    }
    return "11:00";
  });
  const [status, setStatus] = useState(visit?.status || "scheduled");
  const [notes, setNotes] = useState(visit?.notes || "");

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [showQuickClientModal, setShowQuickClientModal] = useState(false);
  const [quickClient, setQuickClient] = useState({
    first_name: "",
    last_name: "",
    date_of_birth: "1960-01-01",
    phone: "",
  });
  const [savingQuickClient, setSavingQuickClient] = useState(false);
  const [quickClientError, setQuickClientError] = useState(null);

  const [showQuickCaregiverModal, setShowQuickCaregiverModal] = useState(false);
  const [quickCaregiver, setQuickCaregiver] = useState({
    first_name: "",
    last_name: "",
    phone: "",
    email: "",
  });
  const [savingQuickCaregiver, setSavingQuickCaregiver] = useState(false);
  const [quickCaregiverError, setQuickCaregiverError] = useState(null);

  useEffect(() => {
    if (clientId) {
      carePlansApi.list({ client: clientId, status: "active" }).then((res) => {
        setClientPlans(res.results || []);
      });
    } else {
      setClientPlans([]);
    }
  }, [clientId]);

  const loadOptions = async () => {
    try {
      const [cRes, cgRes] = await Promise.all([
        clientsApi.list({ page_size: 100 }),
        caregiversApi.list({ page_size: 100 }),
      ]);
      const activeClients = cRes.results || [];
      const activeCaregivers = cgRes.results || [];
      setClients(activeClients);
      setCaregivers(activeCaregivers);
      if (!isEditing && activeClients.length > 0 && !clientId) {
        setClientId(activeClients[0].id);
      }
    } catch {
      setError("Failed to load directory options.");
    } finally {
      setLoadingOptions(false);
    }
  };

  useEffect(() => {
    loadOptions();
  }, []);

  const handleQuickClientSubmit = async (e) => {
    e.preventDefault();
    setQuickClientError(null);
    setSavingQuickClient(true);
    try {
      const newClient = await clientsApi.create({ ...quickClient, status: "active" });
      await loadOptions();
      setClientId(newClient.id);
      setShowQuickClientModal(false);
      setQuickClient({ first_name: "", last_name: "", date_of_birth: "1960-01-01", phone: "" });
    } catch (err) {
      setQuickClientError(err.detail || err.message || "Failed to create client.");
    } finally {
      setSavingQuickClient(false);
    }
  };

  const handleQuickCaregiverSubmit = async (e) => {
    e.preventDefault();
    setQuickCaregiverError(null);
    setSavingQuickCaregiver(true);
    try {
      const newCg = await caregiversApi.create({ ...quickCaregiver, status: "active" });
      await loadOptions();
      setCaregiverId(newCg.id);
      setShowQuickCaregiverModal(false);
      setQuickCaregiver({ first_name: "", last_name: "", phone: "", email: "" });
    } catch (err) {
      setQuickCaregiverError(err.detail || err.message || "Failed to create caregiver.");
    } finally {
      setSavingQuickCaregiver(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      const isoStart = new Date(`${visitDate}T${startTime}:00`).toISOString();
      const isoEnd = new Date(`${visitDate}T${endTime}:00`).toISOString();
      const payload = {
        client: clientId,
        caregiver: caregiverId || null,
        title: title || undefined,
        service_type: serviceType,
        start_time: isoStart,
        end_time: isoEnd,
        status,
        notes,
      };
      if (!isEditing && carePlanId) payload.care_plan = carePlanId;
      if (isEditing) await visitsApi.update(visit.id, payload);
      else await visitsApi.create(payload);
      onSaved();
    } catch (err) {
      setError(err.detail || err.message || "Failed to save visit.");
    } finally {
      setSaving(false);
    }
  };

  const handleCancelVisit = async () => {
    if (!window.confirm("Are you sure you want to cancel this visit?")) return;
    setSaving(true);
    try {
      await visitsApi.update(visit.id, { status: "cancelled" });
      onSaved();
    } catch (err) {
      setError(err.detail || err.message);
    } finally {
      setSaving(false);
    }
  };

  const clientOptions = clients.map((c) => ({
    value: c.id,
    label: `${c.first_name} ${c.last_name}`,
  }));

  const caregiverOptions = [
    { value: "", label: "-- Unassigned --" },
    ...caregivers.map((cg) => ({
      value: cg.id,
      label: `${cg.first_name} ${cg.last_name}`,
    })),
  ];

  const carePlanOptions = [
    { value: "", label: "-- Default Care Tasks --" },
    ...clientPlans.map((cp) => ({
      value: cp.id,
      label: `${cp.title} (${cp.category_display})`,
    })),
  ];

  return (
    <>
      <Modal title={isEditing ? "Edit Visit" : "Schedule New Visit"} onClose={onClose} size="large">
        <Alert tone="error">{error}</Alert>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label>Client *</Label>
                {!isEditing ? (
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    className="h-auto p-0"
                    onClick={() => setShowQuickClientModal(true)}
                  >
                    + Add Client
                  </Button>
                ) : null}
              </div>
              <FormSelect
                id="visit-client"
                value={clientId}
                onValueChange={setClientId}
                options={clientOptions}
                placeholder="Select client…"
                disabled={loadingOptions || isEditing}
              />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label>Caregiver</Label>
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  className="h-auto p-0"
                  onClick={() => setShowQuickCaregiverModal(true)}
                >
                  + Add Caregiver
                </Button>
              </div>
              <FormSelect
                id="visit-caregiver"
                value={caregiverId}
                onValueChange={setCaregiverId}
                options={caregiverOptions}
                placeholder="Select caregiver…"
                disabled={loadingOptions}
              />
            </div>
          </div>

          {!isEditing && clientPlans.length > 0 ? (
            <div className="space-y-1.5">
              <Label>Copy Tasks from Care Plan (Optional)</Label>
              <FormSelect
                id="visit-care-plan"
                value={carePlanId}
                onValueChange={setCarePlanId}
                options={carePlanOptions}
                placeholder="Default care tasks"
              />
            </div>
          ) : null}

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Visit Title</Label>
              <Input
                placeholder="e.g. Morning Care & Meal Prep"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Service Type</Label>
              <Input
                value={serviceType}
                onChange={(e) => setServiceType(e.target.value)}
                required
              />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label>Date *</Label>
              <Input
                type="date"
                value={visitDate}
                onChange={(e) => setVisitDate(e.target.value)}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label>Start Time *</Label>
              <Input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label>End Time *</Label>
              <Input
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                required
              />
            </div>
          </div>

          {isEditing ? (
            <div className="space-y-1.5">
              <Label>Status</Label>
              <FormSelect
                id="visit-status"
                value={status}
                onValueChange={setStatus}
                options={STATUS_OPTIONS}
                placeholder="Select status…"
              />
            </div>
          ) : null}

          <div className="space-y-1.5">
            <Label>Visit Notes</Label>
            <Textarea
              rows={3}
              placeholder="Add care instructions or observations..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          <div className="flex flex-wrap items-center gap-2 pt-2">
            <Button type="submit" disabled={saving}>
              {saving ? "Saving..." : isEditing ? "Update Visit" : "Schedule Visit"}
            </Button>
            {isEditing ? (
              <Button type="button" variant="outline" asChild>
                <Link to={`/visits/${visit.id}`} onClick={onClose}>
                  Open Workspace
                </Link>
              </Button>
            ) : null}
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            {isEditing && status !== "cancelled" ? (
              <Button
                type="button"
                variant="destructive"
                className="ml-auto"
                onClick={handleCancelVisit}
              >
                Cancel Visit
              </Button>
            ) : null}
          </div>
        </form>
      </Modal>

      {showQuickClientModal ? (
        <Modal title="Quick Add Client" onClose={() => setShowQuickClientModal(false)} size="compact">
          <Alert tone="error">{quickClientError}</Alert>
          <form onSubmit={handleQuickClientSubmit} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>First Name *</Label>
                <Input
                  value={quickClient.first_name}
                  onChange={(e) => setQuickClient({ ...quickClient, first_name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label>Last Name *</Label>
                <Input
                  value={quickClient.last_name}
                  onChange={(e) => setQuickClient({ ...quickClient, last_name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label>Date of Birth *</Label>
                <Input
                  type="date"
                  value={quickClient.date_of_birth}
                  onChange={(e) =>
                    setQuickClient({ ...quickClient, date_of_birth: e.target.value })
                  }
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label>Phone</Label>
                <Input
                  type="tel"
                  value={quickClient.phone}
                  onChange={(e) => setQuickClient({ ...quickClient, phone: e.target.value })}
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={savingQuickClient}>
                {savingQuickClient ? "Creating..." : "Save & Select Client"}
              </Button>
              <Button type="button" variant="ghost" onClick={() => setShowQuickClientModal(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </Modal>
      ) : null}

      {showQuickCaregiverModal ? (
        <Modal
          title="Quick Add Caregiver"
          onClose={() => setShowQuickCaregiverModal(false)}
          size="compact"
        >
          <Alert tone="error">{quickCaregiverError}</Alert>
          <form onSubmit={handleQuickCaregiverSubmit} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>First Name *</Label>
                <Input
                  value={quickCaregiver.first_name}
                  onChange={(e) =>
                    setQuickCaregiver({ ...quickCaregiver, first_name: e.target.value })
                  }
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label>Last Name *</Label>
                <Input
                  value={quickCaregiver.last_name}
                  onChange={(e) =>
                    setQuickCaregiver({ ...quickCaregiver, last_name: e.target.value })
                  }
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label>Phone</Label>
                <Input
                  type="tel"
                  value={quickCaregiver.phone}
                  onChange={(e) =>
                    setQuickCaregiver({ ...quickCaregiver, phone: e.target.value })
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={quickCaregiver.email}
                  onChange={(e) =>
                    setQuickCaregiver({ ...quickCaregiver, email: e.target.value })
                  }
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={savingQuickCaregiver}>
                {savingQuickCaregiver ? "Creating..." : "Save & Select Caregiver"}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setShowQuickCaregiverModal(false)}
              >
                Cancel
              </Button>
            </div>
          </form>
        </Modal>
      ) : null}
    </>
  );
}
